import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import {
  Send,
  Loader2,
  Mail,
  FileText,
  ChevronLeft,
  Sparkles,
  X,
  AlertCircle
} from "lucide-react";

// Default compose template
const DEFAULT_COMPOSE_TEMPLATE = {
  subject: "Re: Your submission",
  body: `Hi {{name}},

Thanks for your submission — we're reviewing it and will get back to you shortly.

Best,
{{business_name}}`
};

// Variable resolution helper - resolves variables from submission/contact/settings
const resolveVariables = (text, submission, contact, settings, businessName) => {
  if (!text) return text;
  
  let resolved = text;
  
  // Priority 1: Submission data
  const formData = submission?.form_data || {};
  
  // Extract name with various fallbacks
  const submissionName = submission?.customer_name || 
    formData.name || 
    formData.full_name || 
    formData.client_name ||
    formData.your_name ||
    contact?.name ||
    "";
  
  // Extract email
  const submissionEmail = submission?.customer_email || 
    formData.email ||
    contact?.email ||
    "";
  
  // Extract phone
  const submissionPhone = submission?.customer_phone ||
    formData.phone ||
    formData.mobile ||
    contact?.phone ||
    "";
  
  // First name extraction
  const firstName = submissionName.split(' ')[0] || "";
  
  // Standard variable replacements - Submission/Contact data
  const variables = {
    name: submissionName,
    first_name: firstName,
    email: submissionEmail,
    phone: submissionPhone,
    company: formData.company || formData.company_name || contact?.company || "",
    business_name: businessName || ""
  };
  
  // Priority 2: Contact data overrides (if explicitly provided in contact)
  if (contact) {
    if (contact.name && !submissionName) variables.name = contact.name;
    if (contact.email && !submissionEmail) variables.email = contact.email;
    if (contact.phone && !submissionPhone) variables.phone = contact.phone;
    if (contact.company) variables.company = contact.company;
  }
  
  // Priority 3: Settings tools (scheduling link, etc.)
  if (settings) {
    variables.scheduling_link = settings.scheduling_link || settings.calendar_link || "";
    variables.feedback_link = settings.feedback_link || "";
    variables.payment_link = settings.payment_link || "";
    variables.portal_link = settings.portal_link || "";
    variables.agreement_link = settings.agreement_link || "";
    variables.onboarding_link = settings.onboarding_link || "";
    
    // Custom links
    if (settings.custom_links) {
      Object.entries(settings.custom_links).forEach(([key, value]) => {
        variables[key] = value || "";
      });
    }
  }
  
  // Replace all {{variable}} patterns
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi');
    resolved = resolved.replace(regex, value || "");
  });
  
  // Also handle form data fields directly
  Object.entries(formData).forEach(([key, value]) => {
    if (typeof value === 'string') {
      const regex = new RegExp(`\\{\\{\\s*${key.toLowerCase().replace(/\s+/g, '_')}\\s*\\}\\}`, 'gi');
      resolved = resolved.replace(regex, value);
    }
  });
  
  return resolved;
};

export default function EmailComposerModal({
  open,
  onClose,
  submission,
  contact,
  token,
  templates = [],
  businessName = "",
  mode = "compose", // "compose" | "template"
  selectedTemplateId = null, // Pre-selected template ID
  onEmailSent
}) {
  const [view, setView] = useState(mode); // "compose" | "template" | "editor"
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [integrationSettings, setIntegrationSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  
  // Draft preservation
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [hasDraft, setHasDraft] = useState(false);
  
  // Get recipient email
  const recipientEmail = submission?.customer_email || contact?.email || "";
  const recipientName = submission?.customer_name || contact?.name || "Unknown";
  
  // Fetch integration settings for variable resolution
  const fetchIntegrationSettings = useCallback(async () => {
    if (!token) return;
    setLoadingSettings(true);
    try {
      const response = await axios.get(`${API}/workflow/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch integration settings:", error);
    } finally {
      setLoadingSettings(false);
    }
  }, [token]);
  
  // Load settings and initialize on open
  useEffect(() => {
    if (open) {
      fetchIntegrationSettings();
      setView(mode);
      
      // DEBUG: Log template selection
      console.log("[EmailComposer] Opening modal:", { mode, selectedTemplateId, templatesCount: templates.length });
      
      // Check for existing draft
      if (hasDraft && draft.subject && draft.body) {
        setSubject(draft.subject);
        setBody(draft.body);
        setView("editor");
      } else if (selectedTemplateId && templates.length > 0) {
        // Pre-selected template - find and load it directly
        const template = templates.find(t => 
          t.id === selectedTemplateId || 
          t.template_id === selectedTemplateId ||
          t.name === selectedTemplateId
        );
        
        console.log("[EmailComposer] Looking for template:", selectedTemplateId, "Found:", template);
        
        if (template) {
          setSelectedTemplate(template);
          const resolved = {
            subject: resolveVariables(template.subject || "", submission, contact, integrationSettings, businessName),
            body: resolveVariables(template.body || template.content || "", submission, contact, integrationSettings, businessName)
          };
          console.log("[EmailComposer] Loaded template:", { name: template.name, subject: resolved.subject });
          setSubject(resolved.subject);
          setBody(resolved.body);
          setView("editor");
        } else {
          // Template not found, fall back to compose mode
          console.warn("[EmailComposer] Template not found:", selectedTemplateId);
          const resolved = {
            subject: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.subject, submission, contact, integrationSettings, businessName),
            body: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.body, submission, contact, integrationSettings, businessName)
          };
          setSubject(resolved.subject);
          setBody(resolved.body);
          setView("editor");
        }
      } else if (mode === "compose") {
        // Pre-fill with default template
        const resolved = {
          subject: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.subject, submission, contact, integrationSettings, businessName),
          body: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.body, submission, contact, integrationSettings, businessName)
        };
        setSubject(resolved.subject);
        setBody(resolved.body);
        setView("editor");
      } else {
        // Template mode - show picker
        setSubject("");
        setBody("");
      }
    }
  }, [open, mode, selectedTemplateId, submission, contact, businessName, templates]);
  
  // Update resolved variables when settings load
  useEffect(() => {
    if (integrationSettings && view === "editor" && !selectedTemplate && mode === "compose") {
      const resolved = {
        subject: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.subject, submission, contact, integrationSettings, businessName),
        body: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.body, submission, contact, integrationSettings, businessName)
      };
      setSubject(resolved.subject);
      setBody(resolved.body);
    }
  }, [integrationSettings]);
  
  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Resolve variables in template
    const resolved = {
      subject: resolveVariables(template.subject, submission, contact, integrationSettings, businessName),
      body: resolveVariables(template.body, submission, contact, integrationSettings, businessName)
    };
    
    setSubject(resolved.subject);
    setBody(resolved.body);
    setView("editor");
  };
  
  // Handle close with draft preservation
  const handleClose = () => {
    // Save draft if content exists
    if ((subject || body) && view === "editor") {
      setDraft({ subject, body });
      setHasDraft(true);
    }
    onClose();
  };
  
  // Handle clear draft
  const handleClearDraft = () => {
    setDraft({ subject: "", body: "" });
    setHasDraft(false);
    // Reset to default compose
    const resolved = {
      subject: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.subject, submission, contact, integrationSettings, businessName),
      body: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.body, submission, contact, integrationSettings, businessName)
    };
    setSubject(resolved.subject);
    setBody(resolved.body);
  };
  
  // Send email
  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    
    if (!recipientEmail) {
      toast.error("Recipient email is missing");
      return;
    }
    
    // Verify we have a contact_id (required for the API)
    const contactId = submission?.contact_id || contact?.id;
    if (!contactId) {
      toast.error("Cannot send email: Contact not found for this submission");
      return;
    }
    
    setSending(true);
    try {
      // Use the workflow email send endpoint with subject/body overrides
      // We'll use a generic template_id and override everything
      const response = await axios.post(
        `${API}/workflow/email/send`,
        {
          contact_id: contactId,
          template_id: selectedTemplate?.id || "acknowledgment", // Fallback template
          subject_override: subject,
          body_override: body,
          submission_id: submission?.id // Log to submission's activity timeline
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Log response for debugging
      console.log("Email response:", response);
      
      // Treat any 2xx response as success
      if (response.status >= 200 && response.status < 300) {
        toast.success("Email sent successfully!");
        
        // Clear draft on success
        setDraft({ subject: "", body: "" });
        setHasDraft(false);
        
        // Callback to parent (refresh activity timeline)
        if (onEmailSent) {
          onEmailSent(response.data);
        }
        
        onClose();
      } else {
        // Non-2xx but not an axios error (unlikely)
        console.error("Unexpected response status:", response.status);
        toast.error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      console.error("Error response:", error.response);
      const errorMessage = error.response?.data?.detail || "Failed to send email";
      toast.error(errorMessage);
      // DO NOT silently fail - always show error
    } finally {
      setSending(false);
    }
  };
  
  // Filter templates based on submission mode
  const filteredTemplates = templates.filter(t => {
    // If template has a mode/category, filter by it
    if (t.category && submission?.mode) {
      return t.category === submission.mode || t.category === "all";
    }
    return true;
  });
  
  // Template picker view
  const renderTemplatePicker = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <FileText className="w-4 h-4" />
        <span>Select a template to use</span>
      </div>
      
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No templates available</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-4"
            onClick={() => {
              // Switch to compose mode with default template
              const resolved = {
                subject: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.subject, submission, contact, integrationSettings, businessName),
                body: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.body, submission, contact, integrationSettings, businessName)
              };
              setSubject(resolved.subject);
              setBody(resolved.body);
              setView("editor");
            }}
          >
            <Mail className="w-4 h-4 mr-2" />
            Compose New Email
          </Button>
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-2">
            {filteredTemplates.map((template) => (
              <button
                key={template.id}
                className="w-full text-left p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors"
                onClick={() => handleSelectTemplate(template)}
                data-testid={`template-${template.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{template.name}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {template.subject}
                    </p>
                  </div>
                  {template.category && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      {template.category}
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      )}
      
      <div className="pt-4 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => {
            const resolved = {
              subject: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.subject, submission, contact, integrationSettings, businessName),
              body: resolveVariables(DEFAULT_COMPOSE_TEMPLATE.body, submission, contact, integrationSettings, businessName)
            };
            setSubject(resolved.subject);
            setBody(resolved.body);
            setView("editor");
          }}
        >
          <Mail className="w-4 h-4 mr-2" />
          Or compose a new email
        </Button>
      </div>
    </div>
  );
  
  // Email editor view
  const renderEditor = () => (
    <div className="space-y-4">
      {/* Back button if came from template picker */}
      {mode === "template" && (
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2"
          onClick={() => {
            setView("template");
            setSelectedTemplate(null);
          }}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to templates
        </Button>
      )}
      
      {/* Draft notice */}
      {hasDraft && (
        <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 text-sm text-amber-700">
            <AlertCircle className="w-4 h-4" />
            <span>Draft restored</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-amber-600 hover:text-amber-800"
            onClick={handleClearDraft}
          >
            Clear draft
          </Button>
        </div>
      )}
      
      {/* Recipient */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">To</Label>
        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">{recipientName}</span>
          <span className="text-sm text-muted-foreground">&lt;{recipientEmail}&gt;</span>
        </div>
      </div>
      
      {/* Subject */}
      <div className="space-y-2">
        <Label htmlFor="email-subject" className="text-sm font-medium">Subject</Label>
        <Input
          id="email-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject..."
          className="h-10"
          data-testid="email-subject-input"
        />
      </div>
      
      {/* Body */}
      <div className="space-y-2">
        <Label htmlFor="email-body" className="text-sm font-medium">Message</Label>
        <Textarea
          id="email-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your message..."
          className="min-h-[200px] resize-none"
          data-testid="email-body-input"
        />
      </div>
      
      {/* Variable hints */}
      <div className="text-xs text-muted-foreground bg-slate-50 p-3 rounded-lg">
        <div className="flex items-center gap-1 mb-1">
          <Sparkles className="w-3 h-3" />
          <span className="font-medium">Available variables:</span>
        </div>
        <p className="leading-relaxed">
          {"{{name}}"}, {"{{first_name}}"}, {"{{email}}"}, {"{{phone}}"}, {"{{business_name}}"}, {"{{scheduling_link}}"}
        </p>
      </div>
    </div>
  );
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]" data-testid="email-composer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {view === "template" ? "Select Template" : "Compose Email"}
          </DialogTitle>
          <DialogDescription>
            {view === "template" 
              ? "Choose a template to pre-fill your email"
              : `Send an email to ${recipientName}`
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {loadingSettings ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : view === "template" ? (
            renderTemplatePicker()
          ) : (
            renderEditor()
          )}
        </div>
        
        {view === "editor" && (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={sending}>
              Cancel
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={sending || !subject.trim() || !body.trim() || !recipientEmail}
              data-testid="send-email-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
